/**
 * Rationale Prompt Templates — system + user prompt builders for the
 * `gibbs-rationale-llm-grounding` study (2026-05-04).
 *
 * The grounded variant of `decisionRationale.generateFor` calls an LLM
 * to rewrite the two prose fields (`rankExplanation`, `suggestedNext`)
 * of a `DecisionRationale` object. The four numeric fields stay
 * deterministic; the prompt is asked to ground its prose in those
 * fields and reference no values that aren't in the input.
 *
 * The system prompt declares the schema, the anti-hallucination rule,
 * and the desired tone. The user prompt embeds the structured input
 * + the deterministic template result the LLM is rewriting.
 *
 * @module gibbs/results/rationalePromptTemplates
 */

/**
 * The system prompt enumerates:
 *   - the JSON shape the LLM MUST return,
 *   - the anti-hallucination rule (only reference fields present in
 *     the input),
 *   - the tone (chemist-briefing, specific over generic).
 */
export const SYSTEM_PROMPT = `You are a medicinal-chemistry assistant rewriting two prose fields of a structured rationale record. The user is a medicinal chemist reviewing why a docked compound was ranked the way it was.

You receive a structured input with these fields:
  - compoundId: integer, the 1-based index of the compound
  - rank: integer, the compound's position in the ranking (1 = best)
  - energyBreakdown: object with vdw, electrostatic, total, dockingScore, gninaScore (each may be null)
  - interactionSummary: object with hydrogenBonds (count), hydrophobicContacts (count), piStacking (count), keyResidues (string array)
  - flags: array of {id, severity, citation} — toxicophores or drug-likeness alerts
  - templateRationale: the existing deterministic rationale you are rewriting (for reference only — do not copy it verbatim)

You MUST return a JSON object with EXACTLY these two string fields:
  {
    "rankExplanation": "...",
    "suggestedNext": "..."
  }

Hard rules:
  1. ANTI-HALLUCINATION: reference ONLY values present in the structured input. Do not invent compound IDs, energy values, residue names, interaction counts, or flag IDs. If the input lists keyResidues=["Asn-101", "Phe-273"], do not mention "Tyr-188". If energies are [-9.1, -7.5], do not write "-12.5 kcal/mol".
  2. CITE NUMBERS: when you mention a quantity, cite the actual numeric value from the input (e.g., "GNINA -9.2 kcal/mol", not "good GNINA score").
  3. TONE: write as if briefing a medicinal chemist who already knows the basics. Specific over generic. Avoid filler like "this compound has good drug-like properties" — instead, say which property and what the value is.
  4. LENGTH: rankExplanation MUST be 1–200 chars. suggestedNext MUST be 1–300 chars.
  5. NO META-COMMENTARY: do not say "I am an AI" or "based on the data provided". Just write the rationale.
  6. ACTION REFERENCES: when suggestedNext recommends a next step, name the actual Gibbs action ID (e.g., \`grow-from-all-atoms\`, \`predict-admet\`, \`energy-minimize\`). Surround the actionId in backticks.

Output JSON only. No prose before or after the JSON object.`;

/**
 * Build the user prompt embedding the structured input the LLM should
 * ground its prose in.
 *
 * @param {import('./decisionRationale.js').DecisionRationale} templateResult
 *   The deterministic template result. Provides the compoundId,
 *   numeric fields, and the template prose for reference.
 * @param {{ rank?: number, interactions?: Object }} [context]
 *   Optional extra context (the rank may be passed separately when
 *   the templateResult was generated without it).
 * @returns {string} The user-prompt body.
 */
export function buildUserPrompt(templateResult, context = {}) {
    const rank = context.rank != null ? context.rank : null;
    const payload = {
        compoundId: templateResult.compoundId,
        rank,
        energyBreakdown: templateResult.energyBreakdown,
        interactionSummary: templateResult.interactionSummary,
        flags: templateResult.flags,
        templateRationale: {
            rankExplanation: templateResult.rankExplanation,
            suggestedNext: templateResult.suggestedNext,
        },
    };
    return `Rewrite the two prose fields for this compound:

\`\`\`json
${JSON.stringify(payload, null, 2)}
\`\`\`

Return JSON with rankExplanation and suggestedNext only.`;
}

export default {
    SYSTEM_PROMPT,
    buildUserPrompt,
};
